import React, { useState } from 'react';
import { StandardForm, FormField } from './StandardForm';
import { StandardTable, TableColumn, TableAction } from './StandardTable';
import { UIHelper } from '../../utils/crudHelpers';

// Section configuration interface
export interface SectionConfig<T = any> {
  title: string;
  description?: string;
  icon?: string;

  // Form configuration
  formFields: FormField[];
  formTitle?: string;
  onSubmit: (data: Record<string, any>) => Promise<void>;

  // Table configuration
  data: T[];
  columns: TableColumn<T>[];
  actions?: TableAction<T>[];
  loading?: boolean;
  emptyMessage?: string;
  sortConfig?: { key: string; direction: string } | null;
  onSort?: (key: string) => void;

  // Quick setup configuration (optional)
  quickSetup?: {
    title: string;
    description: string;
    buttonLabel: string;
    onQuickSetup: () => Promise<void>;
  };
}

interface StandardSectionProps<T = any> {
  config: SectionConfig<T>;
}

export function StandardSection<T extends { id: string }>({ config }: StandardSectionProps<T>) {
  const [showForm, setShowForm] = useState(false);
  const [isQuickSetupLoading, setIsQuickSetupLoading] = useState(false);

  const handleFormSubmit = async (data: Record<string, any>) => {
    await config.onSubmit(data);
    setShowForm(false);
  };

  const handleQuickSetup = async () => {
    if (!config.quickSetup) return;

    setIsQuickSetupLoading(true);
    try {
      await config.quickSetup.onQuickSetup();
      alert('✅ Configuración rápida completada exitosamente');
    } catch (error) {
      console.error('Quick setup failed:', error);
      alert('❌ Error en la configuración rápida');
    } finally {
      setIsQuickSetupLoading(false);
    }
  };

  return (
    <div>
      {/* Section Header */}
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>
          {config.icon && `${config.icon} `}
          {config.title}
        </h2>
        {config.description && (
          <p style={{ color: '#666', marginTop: '10px' }}>
            {config.description}
          </p>
        )}
      </div>

      {/* Form Section */}
      {showForm ? (
        <StandardForm
          fields={config.formFields}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowForm(false)}
          title={config.formTitle}
          submitLabel="Agregar"
          submitIcon="➕"
        />
      ) : (
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={() => setShowForm(true)}
            style={{
              ...UIHelper.getStandardButtonStyle('success'),
              marginBottom: '10px'
            }}
          >
            ➕ Agregar {config.title.replace(/s$/, '')}
          </button>
        </div>
      )}

      {/* Data Table */}
      <div style={UIHelper.getStandardCardStyle()}>
        <StandardTable
          data={config.data}
          columns={config.columns}
          actions={config.actions}
          loading={config.loading}
          emptyMessage={config.emptyMessage}
          sortConfig={config.sortConfig}
          onSort={config.onSort}
        />
      </div>

      {/* Quick Setup Section */}
      {config.quickSetup && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: '#e8f5e8',
          borderRadius: '8px'
        }}>
          <h4>🚀 {config.quickSetup.title}</h4>
          <p style={{
            margin: '10px 0',
            fontSize: '14px',
            color: '#555'
          }}>
            {config.quickSetup.description}
          </p>
          <button
            onClick={handleQuickSetup}
            disabled={isQuickSetupLoading}
            style={{
              ...UIHelper.getStandardButtonStyle('success'),
              fontWeight: 'bold',
              opacity: isQuickSetupLoading ? 0.6 : 1
            }}
          >
            {isQuickSetupLoading ? '⏳ Configurando...' : config.quickSetup.buttonLabel}
          </button>
        </div>
      )}
    </div>
  );
}

// Helper function to create a standard entity section configuration
export function createEntitySectionConfig<T extends {
  id: string;
  active?: boolean;
  habilitada?: boolean;
  habilitado?: boolean;
}>(
  title: string,
  icon: string,
  data: T[],
  formFields: FormField[],
  columns: TableColumn<T>[],
  onSubmit: (data: Record<string, any>) => Promise<void>,
  onToggle: (item: T) => Promise<void>,
  onDelete: (item: T) => Promise<void>,
  options: {
    description?: string;
    quickSetup?: SectionConfig<T>['quickSetup'];
    customActions?: TableAction<T>[];
    sortConfig?: { key: string; direction: string } | null;
    onSort?: (key: string) => void;
  } = {}
): SectionConfig<T> {

  // Add status column if not already present
  const hasStatusColumn = columns.some(col => col.key === 'status');
  const finalColumns = hasStatusColumn ? columns : [
    ...columns,
    {
      key: 'status',
      title: 'Estado',
      width: '10%',
      render: (_value: any, item: T) => {
        const isActive = item.active ?? item.habilitada ?? item.habilitado ?? false;
        return (
          <div style={{ textAlign: 'center' }}>
            {isActive ? '✅' : '❌'}
          </div>
        );
      }
    }
  ];

  // Create standard actions
  const standardActions: TableAction<T>[] = [
    ...(options.customActions || []),
    {
      label: '',
      variant: 'secondary',
      onClick: onToggle,
      condition: (item) => {
        // Always show toggle button regardless of current state
        return true;
      }
    },
    {
      label: 'Eliminar',
      variant: 'danger',
      onClick: onDelete
    }
  ];

  return {
    title,
    icon,
    description: options.description,
    formFields,
    onSubmit,
    data,
    columns: finalColumns,
    actions: standardActions,
    quickSetup: options.quickSetup,
    sortConfig: options.sortConfig,
    onSort: options.onSort,
  };
}